export type LocalCareerSection =
  | 'progression'
  | 'vehicleMastery'
  | 'dailyChallenges'
  | 'nightTour';

export interface LocalCareerEnvelope {
  version: 2;
  progression: unknown;
  vehicleMastery: unknown;
  dailyChallenges: unknown;
  nightTour: unknown;
  processedRaceIds: string[];
  updatedAt: number;
}

export const LOCAL_CAREER_STORAGE_KEY = 'nightshift:local-career:v2';
export const LOCAL_CAREER_BACKUP_STORAGE_KEY = 'nightshift:local-career:v2:previous';
const LOCAL_CAREER_MIGRATION_KEY = 'nightshift:local-career:migrated:v2';
const LEGACY_LOCAL_CAREER_STORAGE_KEY = 'nightshift:local-career:v1';
const MAX_PROCESSED_RACE_IDS = 512;
const EMPTY_MIGRATED_SECTION = Object.freeze({});

type LocalCareerSections = Omit<LocalCareerEnvelope, 'version' | 'updatedAt' | 'processedRaceIds'> & {
  processedRaceIds?: readonly string[];
};

export function readLocalCareerEnvelope(
  storage: StoragePort = browserLocalStorage(),
): LocalCareerEnvelope | null {
  try {
    const primary = parseEnvelope(storage.read(LOCAL_CAREER_STORAGE_KEY));
    if (primary) return primary;

    const backup = parseEnvelope(storage.read(LOCAL_CAREER_BACKUP_STORAGE_KEY));
    if (backup) {
      // Repair is best-effort. Returning the validated backup is enough to keep
      // every loader on the same last-known-good transaction.
      storage.write(LOCAL_CAREER_STORAGE_KEY, JSON.stringify(backup));
      return backup;
    }

    // Once any v2 transaction or migration marker exists, an older v1
    // envelope is no longer a recovery source. Falling back to it after both
    // current copies are corrupt can silently resurrect already-spent career
    // progress and re-award races.
    if (hasMigratedCareerEvidence(storage)) return null;
    const legacy = parseLegacyEnvelope(storage.read(LEGACY_LOCAL_CAREER_STORAGE_KEY));
    if (!legacy) return null;
    const migrated: LocalCareerEnvelope = {
      version: 2,
      progression: legacy.progression,
      vehicleMastery: legacy.vehicleMastery,
      dailyChallenges: legacy.dailyChallenges,
      nightTour: legacy.nightTour,
      processedRaceIds: collectProcessedRaceIds(legacy),
      updatedAt: legacy.updatedAt,
    };
    void writeLocalCareerEnvelope(migrated, storage);
    return migrated;
  } catch {
    return null;
  }
}

export function readLocalCareerSection(section: LocalCareerSection): unknown | undefined {
  const envelope = readLocalCareerEnvelope();
  if (envelope) return envelope[section];
  return hasMigratedCareerEvidence() ? EMPTY_MIGRATED_SECTION : undefined;
}

/**
 * One localStorage assignment is the transaction boundary for all accountless
 * career systems. The previous validated assignment is retained as a recovery
 * point, and a migration marker prevents stale standalone keys from replacing
 * newer career data after corruption.
 */
export function writeLocalCareerEnvelope(
  sections: LocalCareerSections,
  storage: StoragePort = browserLocalStorage(),
  clock: PersistenceClock = SYSTEM_PERSISTENCE_CLOCK,
): boolean {
  const next: LocalCareerEnvelope = {
    version: 2,
    progression: sections.progression,
    vehicleMastery: sections.vehicleMastery,
    dailyChallenges: sections.dailyChallenges,
    nightTour: sections.nightTour,
    processedRaceIds: normaliseProcessedRaceIds(
      sections.processedRaceIds ?? collectProcessedRaceIds(sections),
    ),
    updatedAt: clock.now(),
  };
  if (!validEnvelope(next)) return false;
  try {
    const currentRaw = storage.read(LOCAL_CAREER_STORAGE_KEY);
    const current = parseEnvelope(currentRaw);
    if (current && currentRaw) {
      // This recovery copy is useful but must not make the primary write
      // unavailable when a browser quota allows replacement but no extra key.
      storage.write(LOCAL_CAREER_BACKUP_STORAGE_KEY, currentRaw);
    }
    if (!storage.write(LOCAL_CAREER_STORAGE_KEY, JSON.stringify(next))) return false;
    // Mirror the committed transaction. An older valid backup is acceptable
    // if quota prevents this best-effort write.
    storage.write(LOCAL_CAREER_BACKUP_STORAGE_KEY, JSON.stringify(next));
    storage.write(LOCAL_CAREER_MIGRATION_KEY, '1');
    storage.remove(LEGACY_LOCAL_CAREER_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Keeps legacy standalone helpers coherent after migration. `null` means no
 * envelope exists yet; false means its atomic rewrite failed.
 */
export function updateLocalCareerSection(
  section: LocalCareerSection,
  value: unknown,
): boolean | null {
  const current = readLocalCareerEnvelope();
  if (!current) return hasMigratedCareerEvidence() ? false : null;
  return writeLocalCareerEnvelope({
    progression: current.progression,
    vehicleMastery: current.vehicleMastery,
    dailyChallenges: current.dailyChallenges,
    nightTour: current.nightTour,
    processedRaceIds: current.processedRaceIds,
    [section]: value,
  });
}

export function localCareerProcessedRaceIds(): string[] {
  const current = readLocalCareerEnvelope();
  return current
    ? normaliseProcessedRaceIds([
      ...current.processedRaceIds,
      ...collectProcessedRaceIds(current),
    ])
    : [];
}

export function appendLocalCareerRaceId(
  raceIds: readonly string[],
  raceId: string,
): string[] {
  return normaliseProcessedRaceIds([...raceIds, raceId]);
}

function parseEnvelope(raw: string | null): LocalCareerEnvelope | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as unknown;
    return validEnvelope(value) ? value as LocalCareerEnvelope : null;
  } catch {
    return null;
  }
}

function parseLegacyEnvelope(raw: string | null): Omit<LocalCareerEnvelope, 'version' | 'processedRaceIds'> | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as unknown;
    if (
      !isRecord(value)
      || value.version !== 1
      || !validSections(value)
      || typeof value.updatedAt !== 'number'
      || !Number.isFinite(value.updatedAt)
    ) return null;
    return value as unknown as Omit<LocalCareerEnvelope, 'version' | 'processedRaceIds'>;
  } catch {
    return null;
  }
}

function validEnvelope(value: unknown): value is LocalCareerEnvelope {
  return isRecord(value)
    && value.version === 2
    && validSections(value)
    && Array.isArray(value.processedRaceIds)
    && value.processedRaceIds.every(validRaceId)
    && value.processedRaceIds.length <= MAX_PROCESSED_RACE_IDS
    && typeof value.updatedAt === 'number'
    && Number.isFinite(value.updatedAt);
}

function validSections(value: Record<string, unknown>): boolean {
  if (
    !isRecord(value.progression)
    || value.progression.version !== 3
    || !finite(value.progression.xp)
    || !Array.isArray(value.progression.completed)
    || !Array.isArray(value.progression.awardedRaceIds)
    || !isRecord(value.progression.contractProgress)
    || !Array.isArray(value.progression.contractClaims)
    || !isRecord(value.progression.trackMastery)
    || !isRecord(value.progression.rivals)
    || !Array.isArray(value.progression.recentResults)
    || !finite(value.progression.races)
    || !finite(value.progression.wins)
    || !finite(value.progression.bestStyle)
    || !finite(value.progression.bestCombo)
    || !finite(value.progression.updatedAt)
  ) return false;
  if (
    !isRecord(value.vehicleMastery)
    || value.vehicleMastery.version !== 1
    || !Array.isArray(value.vehicleMastery.awardedRaceIds)
    || !isRecord(value.vehicleMastery.records)
  ) return false;
  if (
    !isRecord(value.dailyChallenges)
    || value.dailyChallenges.version !== 1
    || !Array.isArray(value.dailyChallenges.awardedRaceIds)
    || !isRecord(value.dailyChallenges.records)
  ) return false;
  return isRecord(value.nightTour)
    && value.nightTour.version === 1
    && typeof value.nightTour.tourId === 'string'
    && finite(value.nightTour.eventIndex)
    && finite(value.nightTour.playerPoints)
    && finite(value.nightTour.rivalPoints)
    && Array.isArray(value.nightTour.results)
    && typeof value.nightTour.completed === 'boolean'
    && finite(value.nightTour.completions)
    && finite(value.nightTour.bestPoints)
    && finite(value.nightTour.updatedAt)
    && isRecord(value.nightTour.archives);
}

function collectProcessedRaceIds(value: {
  progression: unknown;
  vehicleMastery: unknown;
  dailyChallenges: unknown;
  nightTour: unknown;
}): string[] {
  const candidates: unknown[] = [];
  for (const section of [value.progression, value.vehicleMastery, value.dailyChallenges]) {
    if (isRecord(section) && Array.isArray(section.awardedRaceIds)) {
      candidates.push(...section.awardedRaceIds);
    }
  }
  collectTourRaceIds(value.nightTour, candidates);
  return normaliseProcessedRaceIds(candidates);
}

function collectTourRaceIds(value: unknown, target: unknown[]): void {
  if (!isRecord(value)) return;
  if (Array.isArray(value.results)) {
    for (const result of value.results) {
      if (isRecord(result)) target.push(result.raceId);
    }
  }
  if (!isRecord(value.archives)) return;
  for (const archive of Object.values(value.archives)) {
    if (isRecord(archive)) collectTourRaceIds(archive, target);
  }
}

function normaliseProcessedRaceIds(values: readonly unknown[]): string[] {
  const unique = new Set<string>();
  for (const value of values) {
    if (!validRaceId(value)) continue;
    unique.delete(value);
    unique.add(value);
  }
  return [...unique].slice(-MAX_PROCESSED_RACE_IDS);
}

function hasMigratedCareerEvidence(storage: StoragePort = browserLocalStorage()): boolean {
  try {
    return storage.read(LOCAL_CAREER_MIGRATION_KEY) === '1'
      || storage.read(LOCAL_CAREER_STORAGE_KEY) !== null
      || storage.read(LOCAL_CAREER_BACKUP_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

function validRaceId(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9:_-]{1,160}$/.test(value);
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
import {
  browserLocalStorage,
  SYSTEM_PERSISTENCE_CLOCK,
  type PersistenceClock,
  type StoragePort,
} from './persistence.js';
